import React, { useEffect, useMemo, useRef, useState } from "react";
import { X, Mic2, BadgeCheck, Edit3, Plus } from "lucide-react";
import { usePlayer } from "@/contexts/PlayerContext";
import { useAuth } from "@/contexts/AuthContext";
import { usePaywall } from "@/contexts/PaywallContext";
import { api, mediumArt } from "@/lib/api";
import SubmitLyricsDialog from "@/components/SubmitLyricsDialog";
import SmartImage from "@/components/SmartImage";

const LyricsPanel = ({ open, onClose }) => {
  const { current, progress, seek } = usePlayer();
  const { user, premium } = useAuth();
  const { showPaywall } = usePaywall();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitOpen, setSubmitOpen] = useState(false);
  const [profile, setProfile] = useState(null);
  const scrollRef = useRef(null);
  const activeLineRef = useRef(null);
  const lastFetchKey = useRef(null);
  const [reloadTick, setReloadTick] = useState(0);

  // Lyrics are a Premium feature. The moment a free user opens the
  // panel we close it and surface the paywall — this keeps the
  // call-site contract simple (every place that opens the panel just
  // does ``setShowLyrics(true)`` and gating happens here).
  useEffect(() => {
    if (!open) return;
    const isPremium = !!premium?.active;
    if (!isPremium) {
      showPaywall("lyrics");
      onClose?.();
    }
  }, [open, premium, showPaywall, onClose]);

  // Fetch profile once to determine if current user can submit lyrics
  useEffect(() => {
    if (!user) {
      setProfile(null);
      return;
    }
    api.get("/profile/me").then((r) => setProfile(r.data)).catch(() => setProfile(null));
  }, [user]);

  // Fetch lyrics whenever the active track changes
  useEffect(() => {
    if (!open || !current?.trackName || !current?.artistName) {
      setData(null);
      return;
    }
    const key = `${current.trackId || ""}::${current.trackName}::${current.artistName}::${reloadTick}`;
    if (key === lastFetchKey.current) return;
    lastFetchKey.current = key;
    setLoading(true);
    setData(null);
    api
      .get("/lyrics", {
        params: {
          track_name: current.trackName,
          artist_name: current.artistName,
          album_name: current.collectionName || undefined,
          duration: current.trackTimeMillis ? current.trackTimeMillis / 1000 : undefined,
          track_id: current.trackId ? String(current.trackId) : undefined,
          artist_id: current.artistId ? String(current.artistId) : undefined,
        },
      })
      .then((r) => setData(r.data))
      .catch(() => setData({ found: false }))
      .finally(() => setLoading(false));
  }, [
    open,
    current?.trackId,
    current?.trackName,
    current?.artistName,
    current?.collectionName,
    current?.trackTimeMillis,
    current?.artistId,
    reloadTick,
  ]);

  // Determine the active synced line based on progress (in ms)
  const activeIndex = useMemo(() => {
    const synced = data?.synced || [];
    if (!synced.length) return -1;
    let idx = -1;
    for (let i = 0; i < synced.length; i++) {
      if (synced[i].time_ms <= progress) idx = i;
      else break;
    }
    return idx;
  }, [data, progress]);

  // Auto-scroll active line into view
  useEffect(() => {
    if (!scrollRef.current || !activeLineRef.current) return;
    activeLineRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [activeIndex]);

  if (!open) return null;

  const synced = data?.synced || [];
  const hasSynced = synced.length > 0;
  const plain = data?.plain || "";
  const isArtistSubmitted = data?.source === "artist" || data?.artist_submitted;

  // Can the current user submit lyrics for this track?
  // - Their account must be verified as artist
  // - AND either: (a) their iTunes claim matches current track's artistId, OR
  //   (b) the track is one of their own uploads (track._source === "upload")
  const isMyUpload =
    current?._uploader_id && profile?.user_id && current._uploader_id === profile.user_id;
  const isMyArtistPage =
    profile?.is_artist &&
    profile?.artist_itunes_id &&
    current?.artistId &&
    String(profile.artist_itunes_id) === String(current.artistId);
  const canSubmitLyrics = !!user && (isMyUpload || isMyArtistPage);

  return (
    <>
      <div
        data-testid="lyrics-panel"
        className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm animate-in fade-in duration-200"
        onClick={onClose}
      >
        <div
          className="relative w-full sm:w-[640px] max-h-[85vh] sm:max-h-[80vh] sm:rounded-3xl bg-gradient-to-b from-[#1a1a1a] via-[#0c0c0c] to-black border border-white/10 shadow-2xl flex flex-col overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-white/5 shrink-0 gap-3">
            <div className="flex items-center gap-3 min-w-0">
              {current && (
                <SmartImage
                  src={mediumArt(current.artworkUrl100)}
                  alt={current.trackName || ""}
                  seed={current.trackName || current.artistName || "?"}
                  className="w-12 h-12 rounded-lg overflow-hidden shadow-lg shrink-0"
                  imgClassName="w-full h-full object-cover"
                />
              )}
              <div className="min-w-0">
                <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-[#E2FF31] font-semibold mb-0.5">
                  <Mic2 size={11} /> Lyrics
                  {isArtistSubmitted && (
                    <span
                      data-testid="lyrics-source-artist"
                      className="flex items-center gap-1 bg-[#E2FF31]/15 text-[#E2FF31] px-1.5 py-0.5 rounded text-[9px] font-bold normal-case tracking-normal"
                    >
                      <BadgeCheck size={9} /> from artist
                    </span>
                  )}
                </div>
                <p className="text-sm font-semibold truncate">{current?.trackName}</p>
                <p className="text-xs text-white/50 truncate">{current?.artistName}</p>
              </div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              {canSubmitLyrics && !loading && (
                <button
                  data-testid="lyrics-submit-btn"
                  onClick={() => setSubmitOpen(true)}
                  className="hidden sm:flex items-center gap-1.5 text-xs text-[#E2FF31] hover:text-white bg-[#E2FF31]/10 hover:bg-[#E2FF31]/20 px-3 py-1.5 rounded-full transition"
                  title={isArtistSubmitted ? "Edit lyrics" : "Submit lyrics"}
                >
                  {isArtistSubmitted ? <Edit3 size={12} /> : <Plus size={12} />}
                  {isArtistSubmitted ? "Edit" : "Submit"}
                </button>
              )}
              <button
                data-testid="lyrics-close-btn"
                onClick={onClose}
                className="p-2 rounded-full hover:bg-white/10 transition shrink-0"
                title="Close lyrics"
              >
                <X size={18} />
              </button>
            </div>
          </div>

          {/* Body */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-6 py-8 hide-scrollbar">
            {loading && (
              <div className="text-center text-white/50 text-sm py-12">Looking up lyrics…</div>
            )}

            {!loading && !data?.found && (
              <div className="text-center py-16">
                <Mic2 size={36} className="mx-auto text-white/30 mb-4" />
                <h3 className="font-semibold text-lg mb-1">No lyrics yet</h3>
                <p className="text-white/50 text-sm">
                  Lyrics for &ldquo;{current?.trackName}&rdquo; aren&apos;t in our source yet.
                </p>
                {canSubmitLyrics && (
                  <button
                    data-testid="lyrics-submit-empty-btn"
                    onClick={() => setSubmitOpen(true)}
                    className="mt-5 inline-flex items-center gap-1.5 bg-[#E2FF31] text-black font-semibold rounded-full px-5 py-2 text-sm hover:opacity-90 transition"
                  >
                    <Plus size={14} /> Add your lyrics
                  </button>
                )}
              </div>
            )}

            {!loading && data?.found && data?.instrumental && (
              <div className="text-center py-16 text-white/50">
                <Mic2 size={36} className="mx-auto text-white/30 mb-4" />
                <p className="font-medium">This track is instrumental.</p>
              </div>
            )}

            {!loading && hasSynced && (
              <div className="space-y-4 text-center">
                {synced.map((line, i) => {
                  const active = i === activeIndex;
                  const past = i < activeIndex;
                  return (
                    <p
                      key={i}
                      ref={active ? activeLineRef : null}
                      onClick={() => seek(line.time_ms)}
                      data-testid={`lyrics-line-${i}`}
                      data-active={active ? "true" : "false"}
                      className={`cursor-pointer transition-all leading-relaxed ${
                        active
                          ? "text-white text-2xl font-semibold scale-105"
                          : past
                          ? "text-white/30 text-lg"
                          : "text-white/55 text-lg hover:text-white/80"
                      }`}
                    >
                      {line.text || "♪"}
                    </p>
                  );
                })}
              </div>
            )}

            {!loading && !hasSynced && data?.found && plain && (
              <pre className="whitespace-pre-wrap font-sans text-base leading-relaxed text-white/80 text-center">
                {plain}
              </pre>
            )}
          </div>

          {/* Mobile-only submit button (since header version is hidden on small screens) */}
          {canSubmitLyrics && !loading && data?.found && (
            <button
              data-testid="lyrics-submit-btn-mobile"
              onClick={() => setSubmitOpen(true)}
              className="sm:hidden mx-4 mb-2 flex items-center justify-center gap-1.5 text-xs text-[#E2FF31] bg-[#E2FF31]/10 px-3 py-2 rounded-full"
            >
              {isArtistSubmitted ? <Edit3 size={12} /> : <Plus size={12} />}
              {isArtistSubmitted ? "Edit your lyrics" : "Submit your lyrics"}
            </button>
          )}

          {/* Footer attribution */}
          <div className="text-[10px] text-white/30 text-center py-2 border-t border-white/5">
            {isArtistSubmitted
              ? `Lyrics submitted by ${data.submitted_by_name || "artist"}`
              : "Lyrics by LRCLIB"}
          </div>
        </div>
      </div>

      {submitOpen && (
        <SubmitLyricsDialog
          open={submitOpen}
          onOpenChange={setSubmitOpen}
          track={current}
          existing={
            isArtistSubmitted
              ? { plain: data?.plain, synced: data?.synced }
              : null
          }
          onSaved={() => setReloadTick((t) => t + 1)}
        />
      )}
    </>
  );
};

export default LyricsPanel;
